import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, type Acceptance } from '../lib/api'
import { money, currentPeriod } from '../lib/format'
import { Panel, Alert, Loading, Empty, Tag } from '../components/ui'
import MonthRangePicker from '../components/MonthRangePicker'
import { voidAcceptance } from '../lib/voidAcceptance'

export default function Acceptances() {
  const nav = useNavigate()
  const [from, setFrom] = useState(currentPeriod())
  const [to, setTo] = useState(currentPeriod())
  /** 跨月才需要顯示「月份」欄，單月時每列都一樣沒意義 */
  const multiMonth = from !== to
  const [rows, setRows] = useState<Acceptance[] | null>(null)
  const [error, setError] = useState('')
  const [role, setRole] = useState('')
  const isAdmin = role === 'admin'
  /** 作廢進行中的那一列，避免雙擊送兩次 */
  const [voiding, setVoiding] = useState<number | null>(null)
  /** 批次列印用的勾選 */
  const [selected, setSelected] = useState<Set<number>>(new Set())

  /* 篩選都在前端做：資料已經抓回來了，一個月頂多數十列，
     即時過濾不用防抖也不用 loading，也不必動到線上的 SQL。 */
  /** text＝輸入框當下的字，q＝已套用的關鍵字；按「搜尋」或 Enter 才套用 */
  const [text, setText] = useState('')
  const [q, setQ] = useState('')
  /** 驗收人篩選。選單只列這個區間實際出現過的驗收人，不必另外抓人員主檔 */
  const [reviewer, setReviewer] = useState('')

  useEffect(() => {
    api.get<{ user: { role: string } }>('/me').then((d) => setRole(d.user.role)).catch(() => {})
  }, [])

  const load = () => {
    setRows(null)
    setSelected(new Set())
    api.get<Acceptance[]>(`/acceptances?from=${from}&to=${to}`).then(setRows).catch((e) => setError(e.message))
  }

  useEffect(load, [from, to])

  const doVoid = async (id: number) => {
    setError('')
    setVoiding(id)
    try {
      if (await voidAcceptance(id, setError)) load()
    } finally {
      setVoiding(null)
    }
  }


  /** 這個區間出現過的驗收人，去重後照筆畫排；沒填驗收人的單不列進選單 */
  const reviewers = useMemo(
    () => [...new Set((rows ?? []).map((a) => a.reviewer_name).filter(Boolean) as string[])]
      .sort((a, b) => a.localeCompare(b, 'zh-Hant')),
    [rows]
  )

  const shown = useMemo(() => {
    const kw = q.trim().toLowerCase()
    return (rows ?? []).filter((a) => {
      if (reviewer && a.reviewer_name !== reviewer) return false
      if (!kw) return true
      // 一個搜尋框涵蓋寫手名字、驗收單號、發包單號，不必先選對下拉才找得到
      return [a.writer_name, a.code, a.work_order_code]
        .some((v) => String(v ?? '').toLowerCase().includes(kw))
    })
  }, [rows, q, reviewer])

  /** 作廢單的篇數與金額都已經沖銷掉了，不能算進合計 */
  const effective = useMemo(() => shown.filter((a) => !a.voided_at), [shown])
  const voidedCount = shown.length - effective.length

  const allSelected = shown.length > 0 && shown.every((a) => selected.has(a.id))

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  // 全選只作用在「畫面上看得到的」那些單，篩選後按全選不會把藏起來的也選走
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(shown.map((a) => a.id)))
  }

  const printSelected = () => {
    if (selected.size === 0) return
    nav(`/acceptances/print-batch?ids=${[...selected].join(',')}`)
  }

  const filtering = Boolean(q.trim()) || Boolean(reviewer)
  const rangeLabel = multiMonth ? `${from} ~ ${to}` : '這個月'

  return (
    <>
      <div className="page-head">
        <h2>驗收單列表</h2>
        <span className="hint">月份</span>
        <MonthRangePicker from={from} to={to} onChange={(a, b) => { setFrom(a); setTo(b) }} />
        <div className="spacer" />
        <div className="btn-row">
          <span className="hint" style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
            已勾選 {selected.size} / {shown.length} 張
          </span>
          <button onClick={toggleAll} disabled={shown.length === 0}>
            {allSelected ? '取消全選' : '全選'}
          </button>
          <button disabled={selected.size === 0} onClick={printSelected}>
            批次列印（{selected.size}）
          </button>
          <Link to="/acceptances/new"><button className="primary">新增驗收單</button></Link>
        </div>
      </div>

      <div className="btn-row" style={{ marginBottom: 12 }}>
        <input
          style={{ width: 280 }}
          value={text}
          onChange={(e) => setText(e.target.value)}
          // 打完直接按 Enter 就搜，不必移到滑鼠
          onKeyDown={(e) => { if (e.key === 'Enter') setQ(text) }}
          placeholder="搜尋寫手名字／驗收單號／發包單號"
        />
        <button className="primary" onClick={() => setQ(text)}>搜尋</button>
        <select
          value={reviewer}
          onChange={(e) => setReviewer(e.target.value)}
          style={{ width: 150 }}
          aria-label="驗收人"
        >
          <option value="">全部驗收人</option>
          {reviewers.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        {filtering && (
          <button onClick={() => { setText(''); setQ(''); setReviewer('') }}>
            清除篩選
          </button>
        )}
      </div>

      <Alert kind="error">{error}</Alert>

      <Panel>
        {!rows ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>{rangeLabel}還沒有驗收單</Empty>
        ) : shown.length === 0 ? (
          // 跟「這個區間沒資料」分開講，否則使用者會以為資料不見了
          <Empty>{rangeLabel}共 {rows.length} 張驗收單，但沒有符合篩選條件的</Empty>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  {multiMonth && <th style={{ width: 74 }}>月份</th>}
                  <th>驗收單號</th>
                  <th>發包單號</th>
                  <th>承包人</th>
                  <th>驗收人</th>
                  <th className="num">批次</th>
                  <th>約定交付</th>
                  <th>實際交付</th>
                  <th>驗收日</th>
                  <th className="num">通過篇數</th>
                  <th className="num">核定金額</th>
                  <th>結論</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {shown.map((a) => (
                  // 作廢單留在清單裡標灰就好，憑空消失比標記更容易讓人以為資料出錯
                  <tr
                    key={a.id}
                    style={{
                      ...(a.voided_at ? { opacity: 0.55 } : null),
                      ...(selected.has(a.id) ? { background: 'var(--accent-soft)' } : null),
                    }}
                  >
                    <td>
                      <input
                        type="checkbox"
                        style={{ width: 'auto' }}
                        checked={selected.has(a.id)}
                        onChange={() => toggleOne(a.id)}
                      />
                    </td>
                    {multiMonth && <td>{a.period}</td>}
                    <td>
                      {/* 作廢單也要能點進去看，內容還是要查得到 */}
                      <Link to={`/acceptances/${a.id}/print`}>{a.code}</Link>
                      {a.voided_at && (
                        <span title={`${a.voided_at} ${a.voided_by ?? ''}｜${a.void_reason ?? ''}`}>
                          {' '}<Tag value="已作廢" />
                        </span>
                      )}
                    </td>
                    <td><Link to={`/work-orders/${a.work_order_id}`}>{a.work_order_code}</Link></td>
                    <td>{a.writer_name}</td>
                    <td>{a.reviewer_name}</td>
                    <td className="num">{a.batch_no}</td>
                    <td>{a.due_on}</td>
                    <td style={a.delivered_on && a.due_on && a.delivered_on > a.due_on ? { color: 'var(--bad)' } : undefined}>
                      {a.delivered_on}
                    </td>
                    <td>{a.reviewed_on}</td>
                    <td className="num">{a.passed_qty}</td>
                    <td className="num">{money(a.amount)}</td>
                    <td><Tag value={a.conclusion} /></td>
                    <td>
                      <span className="link-actions">
                        <Link to={`/acceptances/${a.id}/print`}><button>列印</button></Link>
                        {!a.voided_at && <Link to={`/acceptances/${a.id}/edit`}><button>編輯</button></Link>}
                        {isAdmin && !a.voided_at && (
                          <button disabled={voiding === a.id} onClick={() => doVoid(a.id)}>
                            {voiding === a.id ? '作廢中…' : '作廢'}
                          </button>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  {/* 跨月時前面多一欄「月份」，合計列要跟著讓位 */}
                  <td colSpan={multiMonth ? 10 : 9}>
                    合計 {effective.length} 張
                    {voidedCount > 0 && `（另有 ${voidedCount} 張已作廢，不計入）`}
                    {filtering && `（已篩選，區間內共 ${rows.length} 張）`}
                  </td>
                  <td className="num">{effective.reduce((n, a) => n + a.passed_qty, 0)}</td>
                  <td className="num">{money(effective.reduce((n, a) => n + a.amount, 0))}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Panel>
    </>
  )
}
